import { useFormContext, useWatch } from 'react-hook-form';
import { ModalBody, ModalHeader } from '@ds';
import { DateTimePickerInputs } from '@features/components/CaseFormBlocks/components';
import { useReallocationTrialPrograms } from '@features/components/CaseFormBlocks/hooks/useReallocationTrialPrograms';
import { EtaSections } from '@features/components/CaseOverview/components/Eta/EtaSections';
import { CaseTxCenter } from '@lp/features/CaseFormSections';
import { useMemo } from 'react';
import { CaseTxCenterFormType } from './config';
import { isAllowedStatus } from '@/utils/helpers';
import { Case, CaseStatus, ServiceType } from '@/api/apis/case';
import { useDeviceTrackingByCaseIdQuery } from '@/queries/device-tracking';
import { useLogisticsById } from '@/queries/route-planner';
import { EtaType } from '@/types';
import { TxOrChangeReasonInput } from '../components/TxOrChangeReasonInput';
import { EtaFormRow } from '../components/EtaFormRow';

export const EditCaseTxCenterForm = ({
  status,
  isExpeditedCase,
  serviceType,
  caseData,
}: {
  status: CaseStatus;
  isExpeditedCase?: boolean;
  serviceType?: ServiceType;
  caseData: Case;
}) => {
  const { control, getValues } = useFormContext<CaseTxCenterFormType>();
  const reAllocationCaseId = useWatch<CaseTxCenterFormType>({ control, name: 'reAllocationCaseId' });
  const opoRequest = getValues('opoRequest');
  const allDisabled = status === CaseStatus.Declined;

  useReallocationTrialPrograms(reAllocationCaseId);

  const txEtaEnabled =
    !allDisabled ||
    isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.Finished,
      currentStatus: status,
    });

  const txOrDisabled =
    allDisabled ||
    isAllowedStatus({
      statusFrom: CaseStatus.Finished,
      statusTo: CaseStatus.Closed,
      currentStatus: status,
    });

  const txCenterDisabled =
    allDisabled ||
    !isAllowedStatus({
      statusTo: opoRequest || isExpeditedCase ? CaseStatus.OrganOnOCS : CaseStatus.Declined,
      currentStatus: status,
    });

  const txUserDisabled = !isAllowedStatus({
    statusTo: CaseStatus.Finished,
    currentStatus: status,
  });

  const txCenterCoreDisabled = useMemo(() => ({ txCenterId: txCenterDisabled }), [txCenterDisabled]);

  const { data: logisticsData } = useLogisticsById(caseData.logisticsReference?.id);
  const withLocationTracking = !!logisticsData?.isTmdxDriveCompatible && txEtaEnabled;
  const { data: deviceTracking } = useDeviceTrackingByCaseIdQuery(caseData.caseId, withLocationTracking);

  const displayEtaTypes: EtaType[] = useMemo(
    () => (txEtaEnabled ? [EtaType.txCenterSta, EtaType.txCenterEta] : []),
    [txEtaEnabled],
  );

  const txEtaData = useMemo(
    () => ({
      txEtaHospitalDate: caseData.txEtaHospitalDate,
      txEtaHospitalTime: caseData.txEtaHospitalTime,
      txEtaHospitalTimeZone: caseData.txEtaHospitalTimeZone,
      txCenterDetails: caseData.txCenterDetails,
    }),
    [caseData.txEtaHospitalDate, caseData.txEtaHospitalTime, caseData.txEtaHospitalTimeZone, caseData.txCenterDetails],
  );

  return (
    <>
      <ModalHeader>
        <h2>TX Center Info</h2>
      </ModalHeader>
      <ModalBody>
        <CaseTxCenter
          serviceType={serviceType}
          showTxCenterORDateTimeFields={false}
          showWarningNotification
          showRequired={false}
          disabled={txCenterCoreDisabled}
          hasPermission
          txUserDisabled={txUserDisabled}
        >
          {displayEtaTypes.length > 0 && (
            <EtaSections
              displayEtaTypes={displayEtaTypes}
              txEtaData={txEtaData}
              deviceTracking={deviceTracking}
              SectionRowComponent={EtaFormRow}
            />
          )}
          <DateTimePickerInputs
            disabledPast
            hasPermission
            disabledDate={txOrDisabled}
            disabledTime={txOrDisabled}
            disabledTimezone={txOrDisabled}
            control={control}
            title="TX Center OR"
            dateFieldName="recipientHospitalDate"
            dateFieldLabel="Select OR Date"
            timeFieldName="recipientHospitalTime"
            timeFieldLabel="Select OR Time"
            timeZoneFieldName="recipientHospitalTimeZone"
          />
          <TxOrChangeReasonInput isDisabled={txOrDisabled} caseData={caseData} />
        </CaseTxCenter>
      </ModalBody>
    </>
  );
};
